import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, ArrowRight } from "lucide-react";
import TagVisual from "./TagVisual";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const USE_CASES = ["Keys", "Wallet", "Bag", "Remote", "Luggage", "Other"];

const Waitlist = () => {
  const [form, setForm] = useState({ first_name: "", email: "", use_case: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.email.trim()) {
      toast.error("Please enter your first name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/waitlist`, {
        first_name: form.first_name,
        email: form.email,
        use_case: form.use_case || null,
      });
      setSuccess(true);
      toast.success("You're on the list.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your details and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="waitlist"
      data-testid="waitlist-section"
      className="relative py-28 md:py-36 overflow-hidden"
    >
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-10 left-1/3 w-[600px] h-[600px] glow-blue blur-3xl opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-b from-white via-[#FAFAFA] to-white" />
      </div>

      <div className="mx-auto max-w-6xl px-6">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-blue-600 font-medium">Early Access</p>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
              Join the VoloTag Waitlist
            </h2>
            <p className="mt-5 text-zinc-600 text-lg leading-relaxed max-w-md">
              Be first in line when VoloTag starts answering back. Launch updates, early access, and quiet excitement — no spam.
            </p>

            <div className="hidden lg:block mt-12 relative">
              <TagVisual size={280} />
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass rounded-3xl p-8 md:p-10 shadow-2xl"
          >
            {success ? (
              <div className="py-10 text-center" data-testid="waitlist-success">
                <div className="mx-auto w-14 h-14 rounded-full bg-black text-white flex items-center justify-center">
                  <CheckCircle2 className="w-7 h-7" />
                </div>
                <h3 className="mt-6 font-display text-2xl font-semibold tracking-tight">
                  You're on the list.
                </h3>
                <p className="mt-2 text-zinc-600">We'll keep you posted.</p>
                <Button
                  data-testid="waitlist-reset"
                  variant="outline"
                  className="mt-8 rounded-full"
                  onClick={() => {
                    setSuccess(false);
                    setForm({ first_name: "", email: "", use_case: "" });
                  }}
                >
                  Add another
                </Button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5" data-testid="waitlist-form">
                <div>
                  <Label htmlFor="first_name" className="text-xs tracking-widest uppercase text-zinc-500">
                    First name
                  </Label>
                  <Input
                    id="first_name"
                    data-testid="waitlist-first-name"
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    placeholder="Alex"
                    className="mt-2 h-12 rounded-xl bg-white/80 border-zinc-200"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-xs tracking-widest uppercase text-zinc-500">
                    Email address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="waitlist-email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@domain.com"
                    className="mt-2 h-12 rounded-xl bg-white/80 border-zinc-200"
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs tracking-widest uppercase text-zinc-500">
                    What would you use VoloTag for? <span className="text-zinc-400 normal-case tracking-normal">(optional)</span>
                  </Label>
                  <Select
                    value={form.use_case}
                    onValueChange={(v) => setForm({ ...form, use_case: v })}
                  >
                    <SelectTrigger
                      data-testid="waitlist-usecase"
                      className="mt-2 h-12 rounded-xl bg-white/80 border-zinc-200"
                    >
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                      {USE_CASES.map((uc) => (
                        <SelectItem key={uc} value={uc} data-testid={`usecase-opt-${uc.toLowerCase()}`}>
                          {uc}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="waitlist-submit"
                  className="w-full h-[52px] rounded-full bg-black hover:bg-zinc-900 text-white cta-shine group text-[15px] font-medium shadow-[0_12px_30px_-8px_rgba(0,0,0,0.35)] hover:-translate-y-[1px] transition"
                >
                  {loading ? "Joining..." : "Join Waitlist"}
                  {!loading && <ArrowRight className="ml-1 w-4 h-4 transition-transform group-hover:translate-x-1" />}
                </Button>
                <p className="text-center text-xs text-zinc-500">
                  No spam. Just launch updates and early access.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Waitlist;
